import { MaskitoOptions } from "@maskito/core";

/**
 * Adds thousand separators to a number string
 * @param value - The numeric string (without dollar sign or commas)
 * @returns The formatted string with thousand separators
 */
const addThousandSeparators = (value: string): string => {
  const [whole, decimal] = value.split(".");

  // Add commas to the whole number part
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return decimal !== undefined ? `${withCommas}.${decimal}` : withCommas;
};

/**
 * Maskito mask options for a valid dollar amount input up to two decimal places
 * with automatic thousand separators
 * @param includeDollarSymbol - Whether to include the dollar symbol at the beginning
 * @returns Maskito mask options
 * @example
 * 1,234.56
 * 1,234.5
 * 1,234
 * 123
 * 12
 * 1
 */
export const getValidDollarAmountMask = (
  includeDollarSymbol: boolean = true,
): MaskitoOptions => ({
  mask: /^.*$/,
  preprocessors: [
    ({ elementState, data }) => {
      // Remove all non-numeric characters except decimal point
      const cleanData = data.replace(/[^\d.]/g, "");

      return { elementState, data: cleanData };
    },
  ],
  postprocessors: [
    ({ value, selection }, initialState) => {
      // Remove dollar sign and commas for processing
      let cleanValue = value.replace(/[$,]/g, "");

      // If empty, return empty
      if (!cleanValue) {
        return { value: "", selection: [0, 0] };
      }

      // Ensure only one decimal point
      const parts = cleanValue.split(".");
      if (parts.length > 2) {
        cleanValue = parts[0] + "." + parts.slice(1).join("");
      }

      // Limit decimal places to 2
      const [whole, decimal] = cleanValue.split(".");
      // Check if decimal exists (even if empty string) to preserve the decimal point
      const hasDecimal = cleanValue.includes(".");
      const limitedDecimal = decimal ? decimal.slice(0, 2) : "";
      const limitedValue = hasDecimal ? `${whole}.${limitedDecimal}` : whole;

      // Add thousand separators
      const formatted = addThousandSeparators(limitedValue);

      // Add dollar sign if needed
      const finalValue =
        includeDollarSymbol && formatted ? `$${formatted}` : formatted;

      // Auto-remove trailing decimal when deleting
      const isDeletion = finalValue.length < initialState.value.length;
      const hasTrailingDecimal = finalValue.endsWith(".");

      if (isDeletion && hasTrailingDecimal) {
        const withoutDecimal = finalValue.slice(0, -1);
        return {
          value: withoutDecimal,
          selection,
        };
      }

      // Adjust selection for added commas/symbols
      const lengthDiff = finalValue.length - value.length;
      const [start, end] = selection;
      const newSelection: [number, number] = [
        Math.max(0, start + lengthDiff),
        Math.max(0, end + lengthDiff),
      ];

      return { value: finalValue, selection: newSelection };
    },
  ],
});
